import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ExportIcon from "@mui/icons-material/OpenInNew";
import WarningIcon from "@mui/icons-material/Warning";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useCoverageStatus, useHcpProfilePlanInfoExport } from "../../api/useHcpProfileApi";
import { DataWindow, useDataWindows } from "../../api/useMetaDataApi";
import TableHeader from "../../components/Headers/TableHeader";
import SimpleTable from "../../components/Table/SimpleTable";
import { Column, Sort } from "../../components/Table/types";
import Tooltip from "../../components/Tooltip/Tooltip";
import { useIndicationBasedPlanInformationConfiguration } from "../../data/global/useProductConfiguration";
import { useHcpLandscapeState } from "../../data/hcpLandscape/hooks";
import { SortDirection } from "../../generate";
import useGetRole from "../../hooks/useGetRole";
import useHasAdtMktTrxTrans from "../../hooks/useHasAdtMktTrxTrans";

interface Props {
  indication?: string | undefined;
}

const PlanInformation: React.FC<Props> = ({ indication }) => {
  const { t } = useTranslation();
  const { client: selectedClient, id } = useParams() as { client: string; id: string };
  const { data: role } = useGetRole(selectedClient);
  const { data: dataWindows } = useDataWindows(selectedClient);
  const { filters } = useHcpLandscapeState();
  const [sort, setSort] = useState<Sort>({ field: "bob_pct_ovrl", direction: SortDirection.Descending });
  const defaultDataWindow =
    (dataWindows || []).find((dataWindow) => dataWindow === DataWindow.twelveMonths) || DataWindow.twelveMonths;
  const timePeriod = filters.timePeriod || defaultDataWindow;
  const { data, isLoading } = useCoverageStatus(selectedClient, id, timePeriod, sort.field, sort.direction, indication);

  const client_indication_key = useMemo(() => {
    return selectedClient === "BRAFTOVI" ? selectedClient + "_" + indication?.replaceAll(" ", "") : selectedClient;
  }, [selectedClient, indication]);

  const PlanIndicator = "planIndicator" as const;

  const { dataKeys: allowedDataKeys } = useIndicationBasedPlanInformationConfiguration(
    selectedClient,
    indication,
    String(role),
  );

  const hasAdtMktTrxTrans = useHasAdtMktTrxTrans(selectedClient);
  const mktTrxTrans = hasAdtMktTrxTrans
    ? t([`hcpProfile.adtMktTrx.${selectedClient}`])
    : t([
        `hcpProfile.mktTrx.${timePeriod}.${selectedClient}`,
        `hcpProfile.mktTrx.${selectedClient}`,
        `hcpProfile.mktTrx.default`,
      ]);

  const availableColumns: Column[] = useMemo(
    () => [
      {
        label: t("hcpProfile.plan"),
        align: "left",
        dataKey: "m_plan_name",
        tdClasses: "sm:bg-light-blue pl-0",
        component: PlanIndicator,
      },
      {
        label: t("hcpProfile.channel"),
        align: "center",
        dataKey: "channel",
      },
      {
        label: t(
          [`hcpProfile.coverageStatusWithName.${client_indication_key}`, `hcpProfile.coverageStatusWithName.default`],
          {
            name: selectedClient.split("_")[0],
          },
        ),
        align: "center",
        dataKey: "cc_status",
      },
      {
        label: t([`hcpProfile.relativeStatusWithName.${selectedClient}`, `hcpProfile.relativeStatusWithName.default`]),
        align: "center",
        dataKey: "rel_status",
      },
      {
        label: t([
          `hcpProfile.compColumns.${selectedClient}.compCoverageStatus`,
          `hcpProfile.compColumns.default.compCoverageStatus`,
        ]),
        align: "left",
        dataKey: "comp_cc_status",
      },
      {
        label: t(
          [
            `hcpProfile.prodTrx.${timePeriod}.${client_indication_key}`,
            `hcpProfile.prodTrx.${selectedClient}`,
            `hcpProfile.prodTrx.default`,
          ],
          { client: selectedClient },
        ),
        align: "right",
        dataKey: "prod_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: t(
          [
            `hcpProfile.prodNrx.${timePeriod}.${client_indication_key}`,
            `hcpProfile.prodNrx.${selectedClient}`,
            `hcpProfile.prodNrx.default`,
          ],
          { client: selectedClient },
        ),
        align: "right",
        dataKey: "prod_nrx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: t([`hcpProfile.compProdTrx.${selectedClient}`, `hcpProfile.compProdTrx.default`]),
        align: "right",
        dataKey: "comp_prod_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: mktTrxTrans,
        align: "right",
        dataKey: "mkt_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: t([`hcpProfile.prodShare.${selectedClient}`, `hcpProfile.prodShare.default`], {
          client: selectedClient?.split("_")[0],
        }),
        align: "right",
        dataKey: "prod_share",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: t(
          [
            `hcpProfile.percentOfChannelBob.${timePeriod}.${selectedClient}`,
            `hcpProfile.percentOfChannelBob.${timePeriod}.default`,
          ],
          { channel: filters.benificiaryType },
        ),
        align: "right",
        dataKey: "bob_pct_chnl",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: t([
          `hcpProfile.percentOfBob.${timePeriod}.${selectedClient}`,
          `hcpProfile.percentOfBob.${selectedClient}`,
          `hcpProfile.percentOfBob.${timePeriod}.default`,
        ]),
        align: "right",
        dataKey: "bob_pct_ovrl",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
    ],
    [mktTrxTrans, selectedClient, t, timePeriod, client_indication_key, filters.benificiaryType],
  );

  const sortedColumns: Column[] = useMemo(() => {
    // Only include columns that are both in allowedDataKeys AND exist in availableColumns
    return allowedDataKeys
      .map((key: string) => availableColumns.find((col) => col.dataKey === key))
      .filter(Boolean) as Column[];
  }, [availableColumns, allowedDataKeys]);

  const columnNames = sortedColumns.map((column) => t(column.label, column.translationParams));
  const columnKeys = sortedColumns.map((column) => column.dataKey);

  const handleSort = useCallback((newSort: Sort) => {
    setSort(newSort);
  }, []);

  const exportTable = useHcpProfilePlanInfoExport(
    columnKeys,
    columnNames,
    id,
    selectedClient,
    timePeriod,
    sort.field,
    sort.direction,
    indication,
  );

  return (
    <div className="pb-6">
      <TableHeader title={t("hcpProfile.planInformation")}>
        <Tooltip
          placement="bottom-end"
          tip={
            t([
              `hcpProfile.planInformationTooltip.${timePeriod}.${selectedClient}`,
              `hcpProfile.planInformationTooltip.${timePeriod}.default`,
            ]) as string
          }
        >
          <InfoOutlined className="text-primary" />
        </Tooltip>
      </TableHeader>
      <SimpleTable
        columns={sortedColumns}
        data={data}
        loading={isLoading}
        sort={sort}
        handleSort={handleSort}
        tableFooter={
          <tfoot>
            <tr>
              <td colSpan={10} className="sm:text-right p-0">
                <div className="bg-rules flex items-center justify-end">
                  <div className="flex">
                    <button className="btn p-0" disabled={exportTable.isLoading} onClick={() => exportTable.mutate()}>
                      {(exportTable.status === "idle" || exportTable.status === "success") && (
                        <span>{t("common.export")}</span>
                      )}
                      {exportTable.status === "loading" && <span>{t("common.exportLoading")}</span>}
                      {exportTable.status === "error" && (
                        <>
                          <WarningIcon /> <span>{t("common.export")}</span>
                        </>
                      )}
                    </button>

                    <button
                      className="btn p-0"
                      aria-label={t("common.export")}
                      disabled={exportTable.isLoading}
                      onClick={() => exportTable.mutate()}
                    >
                      <ExportIcon />
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        }
      />
    </div>
  );
};

export default PlanInformation;
